import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  alpha,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import FeedbackService from '../../services/feedbackApi'; 
import { useNavigate } from 'react-router-dom';

// Import your logo
import logo from '../../assets/WFHCafe_WObg.png';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const isDark = mode === 'dark';
  
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Theme colors
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  
  // Glass effect styling - EXACTLY like LoginRequiredModal
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : '650px',
    maxHeight: isMobile ? '480px' : '500px', // Slightly taller for text input
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    // Hide scrollbar but allow scrolling
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('🔄 Submitting feedback...');
      
      const feedbackData = {
        userId: user?._id || undefined,
        username: user?.username || 'Anonymous User',
        email: user?.email || 'anonymous@example.com',
        feedback: feedback.trim()
      };

      console.log('📤 Sending data:', feedbackData);
      
      const result = await FeedbackService.submitFeedback(feedbackData);
      console.log('✅ Server response:', result);
      
      if (result.success) {
        toast.success('Thank you for your feedback!');
        setFeedback('');
        onClose();
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('❌ Error details:', error);
      const errorMessage = error?.error || error?.message || 'Failed to submit feedback. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      {/* Main Content Container - Scrollable content */}
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Close Button */}
        <Box sx={{
          position: 'absolute',
          top: isMobile ? 16 : 20,
          right: isMobile ? 16 : 20,
          zIndex: 10,
        }}>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "medium" : "large"} />
          </IconButton>
        </Box>

        {/* Logo - Much Bigger - EXACTLY like LoginRequiredModal */}
        <Box
          component="img"
          src={logo}
          alt="WFHCafe Logo"
          sx={{
            height: isMobile ? 120 : 180,
            width: 'auto',
            mb: isMobile ? 3 : 4,
            mx: 'auto',
            transition: 'all 0.3s ease',
            filter: isDark ? 'brightness(1.1)' : 'none',
            '&:hover': {
              transform: 'scale(1.05)',
            }
          }}
        />
        
        {/* Text Content */}
        <Box sx={{
          textAlign: 'center',
          mb: isMobile ? 3 : 4,
          flex: 1,
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '1.4rem' : '1.75rem',
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            WFHCafe Private Beta Feedback
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.85) : alpha(deepGreen, 0.8),
              fontSize: isMobile ? '0.95rem' : '1rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Welcome to WFHCafe Private Beta! We're excited to have you on board.
            Your feedback is incredibly valuable to us as we shape the future of our platform.
          </Typography>
        </Box>

        {/* Feedback Input */}
        <TextField
          multiline
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Tell us what you think... Share your experience, report bugs, or suggest features"
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.02)',
              borderColor: alpha(isDark ? goldColor : deepGreen, 0.3),
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: goldColor,
                borderWidth: '2px',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: goldColor,
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '0.9rem' : '1rem',
              '&::placeholder': {
                color: isDark ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5),
              }
            },
          }}
          inputProps={{
            maxLength: 2000,
          }}
        />

        {/* Character Counter */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
            textAlign: 'right',
            mb: 3,
          }}
        >
          {feedback.length}/2000 characters
        </Typography>

        {/* Submit Button - Single button like LoginRequiredModal but centered */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 'auto',
        }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || !feedback.trim()}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              background: isDark ? 'rgba(255,255,255,0.15)' : lokiGradient,
              color: isDark ? '#fff' : goldColor,
              fontWeight: 700,
              fontSize: isMobile ? '0.9rem' : '1rem',
              px: isMobile ? 4 : 5,
              py: isMobile ? 0.75 : 1,
              minWidth: '180px',
              border: `2px solid ${alpha(goldColor, 0.3)}`,
              '&:hover': {
                background: isDark ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                opacity: 0.5,
                transform: 'none',
              }
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FeedbackModal;